import axios, { AxiosResponse } from 'axios';
import { serverAxios } from '../../http';
import { IAddressAutocompleteResponse } from 'models/http/google';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { AddressTypes } from 'utils/enums/address';
import { decodePolyline } from 'pages/Trips/Trips.utils';
import { FacilityTypes, LocationTypes } from 'models/http/location';

// ---------- Types ----------
type RoutingEngine = 'osrm' | 'valhalla' | 'auto';

type RequestRouteOptions = {
  engine?: RoutingEngine; // default 'auto'
  costing?: 'auto' | 'pedestrian' | 'bicycle' | 'truck' | 'bus'; // Valhalla only
  timeout?: number;
  abortController?: AbortController;
  osrmBaseUrl?: string; // optional manual override
  valhallaBaseUrl?: string; // optional manual override
};

type OSRMRouteResponse = { routes?: Array<{ geometry?: string }> };
type ValhallaLeg = { shape?: string };
type ValhallaRouteResponse = { trip?: { legs?: ValhallaLeg[] } };

type RawEntry = {
  name: string;
  engine: RoutingEngine;
  url: string;
  polygon?: Array<[number, number]>; // [lat,lng]
  bbox?: [number, number, number, number]; // [minLat,minLng,maxLat,maxLng] (optional)
};

type NormalizedEntry = RawEntry & { bbox: [number, number, number, number] };

// ---------- Clean axios for map servers (NO auth/cookies/custom headers) ----------
const routeAxios = axios.create({
  withCredentials: false, // avoid sending cookies
  headers: {
    // simple headers only to avoid unnecessary preflight for GET
    Accept: 'application/json',
  },
});

// ---------- Manifest loader (remote) ----------
let manifestCache: NormalizedEntry[] | null = null;
let manifestPromise: Promise<NormalizedEntry[]> | null = null;

function normalizeManifest(raw: RawEntry[]): NormalizedEntry[] {
  return raw.map((e: RawEntry): NormalizedEntry => {
    if (e.bbox) {
      return { ...e, bbox: e.bbox };
    }

    if (!e.polygon?.length) {
      // no polygon: assume global coverage
      return { ...e, bbox: [-90, -180, 90, 180] };
    }

    const lats = e.polygon.map((p) => p[0]);
    const lngs = e.polygon.map((p) => p[1]);
    const bbox: [number, number, number, number] = [Math.min(...lats), Math.min(...lngs), Math.max(...lats), Math.max(...lngs)];

    return { ...e, bbox };
  });
}

async function getManifest(): Promise<NormalizedEntry[]> {
  if (manifestCache) {
    return manifestCache;
  }

  if (!manifestPromise) {
    manifestPromise = (async () => {
      try {
        const res = await routeAxios.get<RawEntry[]>('https://maps.moovex.ai/maps.json');
        const raw = res.data ?? [];
        const normalized = normalizeManifest(raw);

        manifestCache = normalized;

        return normalized;
      } catch {
        // On failure, treat as "no manifest" so fallbacks still work
        manifestCache = [];

        return [];
      } finally {
        manifestPromise = null;
      }
    })();
  }

  return manifestPromise;
}

// ---------- Helpers ----------
function inBbox([minLat, minLng, maxLat, maxLng]: [number, number, number, number], p: { lat: number; lng: number }): boolean {
  return p.lat >= minLat && p.lat <= maxLat && p.lng >= minLng && p.lng <= maxLng;
}

// ray-casting for polygon (lat,lng)
function inPolygon(poly: Array<[number, number]>, p: { lat: number; lng: number }): boolean {
  if (!poly?.length) {
    return true; // treat missing polygon as global coverage
  }

  let inside = false;

  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i]; // yi=lat, xi=lng
    const [yj, xj] = poly[j];
    const intersect = yi > p.lat !== yj > p.lat && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi + 1e-12) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

function covers(entry: NormalizedEntry, waypoints: { lat: number; lng: number }[]): boolean {
  return waypoints.every((p) => inBbox(entry.bbox, p) && inPolygon(entry.polygon ?? [], p));
}

function mergeValhallaLegShapes(legs: ValhallaLeg[]): Array<[number, number]> {
  const out: Array<[number, number]> = [];

  legs.forEach((leg, i) => {
    if (!leg.shape) {
      return;
    }

    const pts = decodePolyline(leg.shape, 6);

    if (!pts.length) {
      return;
    }

    if (i > 0 && out.length) {
      out.push(...pts.slice(1));
    } else {
      out.push(...pts);
    }
  });

  return out;
}

function isLatLngTuple(p: [number, number]): boolean {
  return Number.isFinite(p[0]) && Number.isFinite(p[1]) && p[0] >= -90 && p[0] <= 90 && p[1] >= -180 && p[1] <= 180;
}

function allWithinLatLngBounds(pts: Array<[number, number]>): boolean {
  return pts.length >= 2 && pts.every(isLatLngTuple);
}

// region cache key (~11km buckets)
const r = (n: number) => n.toFixed(1);

const regionKey = (wps: { lat: number; lng: number }[], engine: RoutingEngine) =>
  `${engine}:${r(wps[0].lat)},${r(wps[0].lng)}|${r(wps[wps.length - 1].lat)},${r(wps[wps.length - 1].lng)}`;

class GeoLocationHttp {
  private static route = '/geolocation';

  // cache to avoid re-selecting on every drag
  private static selCache = new Map<string, { engine: RoutingEngine; baseUrl: string; entryName?: string; t: number }>();
  private static CACHE_TTL = 10 * 60 * 1000;

  private static tryReuse(
    key: string,
    waypoints: { lat: number; lng: number }[],
    manifest: NormalizedEntry[],
  ): { engine: RoutingEngine; baseUrl: string } | null {
    const hit = this.selCache.get(key);

    if (!hit || Date.now() - hit.t > this.CACHE_TTL) {
      return null;
    }

    if (!hit.entryName) {
      return { engine: hit.engine, baseUrl: hit.baseUrl };
    }

    const entry = manifest.find((e) => e.name === hit.entryName);

    if (entry && covers(entry, waypoints)) {
      return { engine: hit.engine, baseUrl: hit.baseUrl };
    }

    return null;
  }

  private static selectEngineLocal(
    waypoints: { lat: number; lng: number }[],
    opts: RequestRouteOptions,
    manifest: NormalizedEntry[],
  ): { engine: RoutingEngine; baseUrl: string; entryName?: string } {
    const prefer = opts.engine ?? 'auto';

    // manifest-based selection (no extra network)
    if (manifest.length) {
      const orders: RoutingEngine[] = prefer === 'auto' ? ['osrm', 'valhalla'] : [prefer, prefer === 'osrm' ? 'valhalla' : 'osrm'];

      for (const eng of orders) {
        const candidates = manifest.filter((m) => m.engine === eng);

        for (const entry of candidates) {
          if (covers(entry, waypoints)) {
            return { engine: eng, baseUrl: entry.url, entryName: entry.name };
          }
        }
      }
    }

    // fallbacks from manifest (no hardcoded URLs)
    if (prefer === 'valhalla') {
      const v = opts.valhallaBaseUrl || manifest.find((m) => m.engine === 'valhalla')?.url;

      if (v) {
        return { engine: 'valhalla', baseUrl: v };
      }
    }

    const o = opts.osrmBaseUrl || manifest.find((m) => m.engine === 'osrm')?.url;

    if (!o) {
      throw new Error('No OSRM/Valhalla endpoint available in manifest or overrides.');
    }

    return { engine: 'osrm', baseUrl: o };
  }

  private static async resolveRouter(
    waypoints: { lat: number; lng: number }[],
    opts: RequestRouteOptions,
  ): Promise<{ engine: RoutingEngine; baseUrl: string }> {
    const manifest = await getManifest();
    const key = regionKey(waypoints, opts.engine ?? 'auto');
    const reuse = this.tryReuse(key, waypoints, manifest);

    if (reuse) {
      return reuse;
    }

    const selected = this.selectEngineLocal(waypoints, opts, manifest);

    this.selCache.set(key, { ...selected, t: Date.now() });

    return { engine: selected.engine, baseUrl: selected.baseUrl };
  }

  // -------------------- Public APIs --------------------
  public async autoComplete(
    address: string,
    autocompleteType?: AddressTypes,
    locationTypes?: LocationTypes[],
    facilityTypes?: FacilityTypes[],
    abortController?: AbortController,
  ): Promise<IAddressAutocompleteResponse[]> {
    return serverAxios
      .post(
        `${GeoLocationHttp.route}/autocomplete/`,
        { address, addressType: autocompleteType, locationTypes, facilityTypes },
        { signal: abortController?.signal, timeout: HttpTimeoutPriority.Medium },
      )
      .then((res: AxiosResponse<IAddressAutocompleteResponse[]>) => res.data);
  }

  /** Unified route request (auto-selects OSRM/Valhalla; uses remote maps manifest) */
  static async requestRoute(waypoints: { lat: number; lng: number }[], opts: RequestRouteOptions = {}): Promise<Array<[number, number]>> {
    if (!waypoints || waypoints.length < 2) {
      throw new Error('Routing: need at least 2 waypoints');
    }

    const { engine, baseUrl } = await this.resolveRouter(waypoints, opts);

    if (engine === 'valhalla') {
      return this.requestValhallaRouteWithBase(
        baseUrl,
        waypoints,
        opts.costing ?? 'auto',
        opts.abortController,
        opts.timeout ?? HttpTimeoutPriority.Medium,
      );
    }

    return this.requestOsrmRouteWithBase(baseUrl, waypoints, opts.abortController, opts.timeout ?? HttpTimeoutPriority.Medium);
  }

  // ---------------- Engine callers ----------------
  static async requestOsrmRouteWithBase(
    baseUrl: string,
    waypoints: { lat: number; lng: number }[],
    abortController?: AbortController,
    timeout?: number,
  ): Promise<Array<[number, number]>> {
    const base = baseUrl.replace(/\/+$/, '');
    const coordString = waypoints.map((w) => `${w.lng},${w.lat}`).join(';');
    const url = `${base}/route/v1/driving/${coordString}`;

    const res = await routeAxios.get<OSRMRouteResponse>(url, {
      params: {
        overview: 'full',
        geometries: 'polyline6',
        alternatives: false,
        continue_straight: true,
      },
      signal: abortController?.signal,
      timeout,
    });

    const geometry = res.data?.routes?.[0]?.geometry;

    if (!geometry) {
      throw new Error('OSRM: missing route geometry');
    }

    // Try precision 6 first; validate; if invalid, fallback to 5
    let decoded = decodePolyline(geometry, 6);

    if (!allWithinLatLngBounds(decoded)) {
      decoded = decodePolyline(geometry, 5);
    }

    if (!allWithinLatLngBounds(decoded)) {
      throw new Error('OSRM: failed to decode route geometry');
    }

    return decoded;
  }

  static async requestValhallaRouteWithBase(
    baseUrl: string,
    waypoints: { lat: number; lng: number }[],
    costing: RequestRouteOptions['costing'] = 'auto',
    abortController?: AbortController,
    timeout?: number,
  ): Promise<Array<[number, number]>> {
    const base = baseUrl.replace(/\/+$/, '');
    const url = `${base}/route`;

    const body = {
      locations: waypoints.map((w) => ({ lat: w.lat, lon: w.lng, type: 'break_through' })),
      costing,
      shape_format: 'polyline6',
      alternates: 0,
      directions_options: { units: 'kilometers' },
    };

    const res = await routeAxios.post<ValhallaRouteResponse>(url, body, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      signal: abortController?.signal,
      timeout,
    });

    const legs = res.data?.trip?.legs || [];

    if (!legs.length) {
      throw new Error('Valhalla: missing legs/shape');
    }

    const merged = mergeValhallaLegShapes(legs);

    if (!allWithinLatLngBounds(merged)) {
      throw new Error('Valhalla: failed to decode route geometry');
    }

    return merged;
  }
}

export default GeoLocationHttp;
